import * as Sentry from '@sentry/vue'
import { Integrations } from '@sentry/tracing'
import { BASE_URL } from 'src/common/config'

const release = require('../release')

const warningCache = new Map()
const warningThreshold = 2
const timeLimit = 60000

export default async ({ Vue, router }) => {
  const dsn =
    process.env.SENTRY_KEY && process.env.SENTRY_VERSION && process.env.NODE_ENV === 'production'
      ? `${location.protocol}//${process.env.SENTRY_KEY}@${location.host}${BASE_URL}/sen/try/${process.env.SENTRY_VERSION}`
      : null

  if (dsn) {
    try {
      const environment = process.env.ENVIRONMENT
      const sentryConfig = {
        Vue,
        dsn,
        tracesSampleRate: process.env.ENVIRONMENT === 'development' ? 0.5 : 0.02,
        integrations: [
          new Integrations.BrowserTracing({
            routingInstrumentation: Sentry.vueRouterInstrumentation(router),
          }),
        ],
        environment,
        normalizeDepth: 10,
        debug: false,
        release,
        trackComponents: true,
        logErrors: true,
        denyUrls: [
          /^https?:\/\/mc\.yandex\.ru/i,
          /^https?:\/\/google-analytics\.com/i,
          /^https?:\/\/stats\.g\.doubleclick\.net/i,
          /^https?:\/\/s\.*sptpub\.com/i,
          /coena-seruance-all/i,

          // COMMON FROM DOC
          /graph\.facebook\.com/i,
          /connect\.facebook\.net\/en_US\/all\.js/i,
          /eatdifferent\.com\.woopra-ns\.com/i,
          /static\.woopra\.com\/js\/woopra\.js/i,
          /extensions\//i,
          /^chrome:\/\//i,
          /127\.0\.0\.1:4001\/isrunning/i,
          /webappstoolbarba\.texthelp\.com\//i,
          /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
        ],
        ignoreErrors: [
          'NavigationDuplicated',
          'JSONP request failed',
          'Non-Error exception captured',
          'Non-Error promise rejection captured',
          // Random plugins/extensions
          'top.GLOBALS',
          // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
          'originalCreateNotification',
          'canvas.contentDocument',
          'MyApp_RemoveAllHighlights',
          'http://tt.epicplay.com',
          "Can't find variable: ZiteReader",
          'jigsaw is not defined',
          'ComboSearch is not defined',
          'http://loading.retry.widdit.com/',
          'atomicFindClose',
          // Facebook borked
          'fb_xd_fragment',
          // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
          // reduce this. (thanks @acdha)
          // See http://stackoverflow.com/questions/4113268
          'bmi_SafeAddOnload',
          'EBCallBackMessageReceived',
          // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
          'conduitPage',
        ],
        beforeBreadcrumb(breadcrumb) {
          if (breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') {
            const URLS = [
              'mc.yandex.ru',
              'google-analytics.com',
              '/api/v3/gql/',
              'sptpub.com',
              'analytics.google.com',
              '/fp',
            ]

            if (breadcrumb.data?.url && URLS.some((substring) => breadcrumb.data.url.includes(substring))) {
              return null
            }

            if (breadcrumb.category === 'xhr' && breadcrumb.data?.status_code && breadcrumb.data.status_code < 300) {
              return null
            }
          }

          if (breadcrumb.category === 'navigation' && breadcrumb.level === 'info') {
            const to = breadcrumb.data?.to
            const from = breadcrumb.data?.from

            if (to && to === from) {
              return null
            }
          }

          if (breadcrumb.category === 'console') {
            if (breadcrumb.level === 'info') {
              if (breadcrumb.message?.startsWith('DEBUG:')) {
                return breadcrumb
              }
              return null
            }

            if (breadcrumb.level === 'warning') {
              const message = breadcrumb.message || ''
              const now = Date.now()

              if (warningCache.has(message)) {
                const { count, timestamp } = warningCache.get(message)
                if (now - timestamp < timeLimit && count >= warningThreshold) {
                  return null
                }
                warningCache.set(message, { count: count + 1, timestamp })
              } else {
                warningCache.set(message, { count: 1, timestamp: now })
              }

              return breadcrumb
            }
          }

          return breadcrumb
        },
        beforeSend(event) {
          try {
            if (!event?.exception?.values) {
              return event
            }

            if (event.exception && event.exception.values) {
              const frames = event.exception.values[0].raw_stacktrace.frames

              const isThirdPartyError = frames.some((frame) => {
                const absPath = frame?.abs_path

                return process.env.CDN_DOMAIN
                  ? !absPath.includes(location.host) && !absPath.includes(process.env.CDN_DOMAIN)
                  : !absPath.includes(location.host)
              })

              if (isThirdPartyError) {
                return null
              }
            }

            const EXCLUDE_ERRORS_BY_TYPE = ['ChunkLoadError', 'NavigationDuplicated', 'Network Error']
            const excludeEventByType = event.exception.values.some((ex) => EXCLUDE_ERRORS_BY_TYPE.includes(ex.type))
            if (excludeEventByType) {
              return null
            }

            const EXCLUDE_ERRORS_BY_VALUES = [
              'timeout of ',
              'ResizeObserver loop',
              'Request failed with status code 50',
              'Received status code 50',
              'Navigation cancelled',
              'Loading CSS chunk',
              'The operation is insecure',
              'Network Error',
              'Request aborted',
              // QTabs quasar error
              "Cannot read properties of undefined (reading 'scrollWidth')",
            ]
            const excludeEventByValues = event.exception.values.some((ex) =>
              EXCLUDE_ERRORS_BY_VALUES.some((error) => ex.value.includes(error))
            )

            if (excludeEventByValues) {
              return null
            }
          } catch (e) {
            return event
          }
          return event
        },
      }

      Sentry.init(sentryConfig)
    } catch (e) {
      console.error('error sentry: ', e)
    }
  }
}
